/**
 * Pure tree/selection helpers for `TargetingTreeSelect`. Kept out of the
 * component module so it only exports components (fast refresh).
 */

export interface TargetingTreeNode {
  id: string
  name: string
  kind?: string
  action_scene?: string
  children?: TargetingTreeNode[]
}

export interface TargetingSelectionItem {
  id: string
  name: string
}

function collectIds(node: TargetingTreeNode): string[] {
  const kids = node.children ?? []
  return [node.id, ...kids.flatMap(collectIds)]
}

export function filterTree(
  nodes: TargetingTreeNode[],
  query: string,
): TargetingTreeNode[] {
  const q = query.trim().toLowerCase()
  if (!q) return nodes
  const out: TargetingTreeNode[] = []
  for (const node of nodes) {
    const kids = filterTree(node.children ?? [], q)
    if (node.name.toLowerCase().includes(q) || kids.length > 0) {
      out.push({ ...node, children: kids })
    }
  }
  return out
}

export function indexTree(nodes: TargetingTreeNode[]) {
  const byId = new Map<string, TargetingTreeNode>()
  const parentOf = new Map<string, string>()
  const walk = (list: TargetingTreeNode[], parentId?: string) => {
    for (const n of list) {
      byId.set(n.id, n)
      if (parentId) parentOf.set(n.id, parentId)
      walk(n.children ?? [], n.id)
    }
  }
  walk(nodes)
  return { byId, parentOf }
}

function pathFromAncestor(
  ancestorId: string,
  nodeId: string,
  parentOf: Map<string, string>,
): string[] {
  const up: string[] = [nodeId]
  let cur = nodeId
  while (cur !== ancestorId) {
    const parent = parentOf.get(cur)
    if (!parent) return [nodeId]
    up.push(parent)
    cur = parent
  }
  return up.reverse()
}

function nearestSelectedAncestor(
  id: string,
  selected: Set<string>,
  parentOf: Map<string, string>,
): string | null {
  let cur = parentOf.get(id)
  while (cur) {
    if (selected.has(cur)) return cur
    cur = parentOf.get(cur)
  }
  return null
}

/** Selected directly, or covered because an ancestor is selected. */
export function isCovered(
  id: string,
  selected: Set<string>,
  parentOf: Map<string, string>,
): boolean {
  if (selected.has(id)) return true
  return nearestSelectedAncestor(id, selected, parentOf) != null
}

export function selectionState(
  node: TargetingTreeNode,
  selected: Set<string>,
  parentOf: Map<string, string>,
): "none" | "some" | "all" {
  if (isCovered(node.id, selected, parentOf)) return "all"
  const ids = collectIds(node)
  // Count coverage of leaf-most selected nodes under this branch.
  const hit = ids.filter((id) => selected.has(id)).length
  if (hit === 0) return "none"
  return "some"
}

/**
 * Ads Manager style: store the highest fully-selected nodes only.
 * - Whole group selected → parent id (one chip)
 * - Partial group → child ids (chips for lower levels)
 */
export function collapseSelection(
  selectedIds: string[],
  tree: TargetingTreeNode[],
  labels: Record<string, string>,
): TargetingSelectionItem[] {
  const { byId, parentOf } = indexTree(tree)
  const selected = new Set(
    selectedIds.filter((id) => byId.has(id) || labels[id]),
  )

  // Drop descendants when an ancestor is already selected.
  for (const id of [...selected]) {
    if (nearestSelectedAncestor(id, selected, parentOf)) {
      selected.delete(id)
    }
  }

  // Promote full sibling sets up to their parent (repeat until stable).
  let changed = true
  while (changed) {
    changed = false
    for (const node of byId.values()) {
      const kids = node.children ?? []
      if (kids.length === 0 || selected.has(node.id)) continue
      if (kids.every((child) => selected.has(child.id))) {
        for (const child of kids) selected.delete(child.id)
        selected.add(node.id)
        changed = true
      }
    }
  }

  return [...selected].map((id) => ({
    id,
    name: byId.get(id)?.name ?? labels[id] ?? id,
  }))
}

export function nextSelectionAfterToggle(
  node: TargetingTreeNode,
  tree: TargetingTreeNode[],
  selectedIds: string[],
  labels: Record<string, string>,
): TargetingSelectionItem[] {
  const { byId, parentOf } = indexTree(tree)
  const selected = new Set(selectedIds)
  const ancestor = nearestSelectedAncestor(node.id, selected, parentOf)

  if (selected.has(node.id)) {
    selected.delete(node.id)
  } else if (ancestor) {
    // Unchecking a child under a selected parent → keep siblings, drop this branch.
    selected.delete(ancestor)
    const path = pathFromAncestor(ancestor, node.id, parentOf)
    for (let i = 0; i < path.length - 1; i += 1) {
      const parent = byId.get(path[i])
      const nextId = path[i + 1]
      for (const child of parent?.children ?? []) {
        if (child.id !== nextId) selected.add(child.id)
      }
    }
  } else {
    // Selecting a node covers its descendants — drop any explicit child picks.
    for (const id of collectIds(node)) {
      if (id !== node.id) selected.delete(id)
    }
    selected.add(node.id)
  }

  return collapseSelection([...selected], tree, labels)
}
